import React from 'react';
import { LiveCall } from 'redux/slices/liveCalls.slice';
import LiveCallItemWrapper from './LiveCallsItemWrapper';
import { useInfoStatus } from 'hooks/useInfoStatus';
import { useDrag, useDrop } from 'react-dnd';

interface MemoizedCallItemProps {
  call: LiveCall;
  onRecord: (callUUID: string) => void;
  onHangUp: (callUUID: string) => void;
  onClickCall: (call: LiveCall) => void;
  onTransfer?: (callUUID: string, ext: string, destination: string, callees?: any[], callerIDNumber?: string, callerIDName?: string) => void;
  onDndTransfer?: (callUUID: string, targetExt: string, callees: any[], callerIDNumber: string, callerIDName: string, destination: string) => void;
}

const MemoizedCallItem: React.FC<MemoizedCallItemProps> = ({
  call,
  onRecord,
  onHangUp,
  onClickCall,
  onTransfer,
  onDndTransfer
}) => {
  const { getInfoText } = useInfoStatus();

  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: 'CALL',
    item: { callUUID: call.callUUID, callees: call.callees, callerIDNumber: call.callerIDNumber, callerIDName: call.callerIDName, destination: call.destination },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [call]);

  const [{ isOver }, dropRef] = useDrop(() => ({
    accept: 'EXT',
    drop: (item: any) => {
      const targetExt = item?.ext;
      if (targetExt) {
        onDndTransfer?.(call.callUUID, targetExt, call.callees, call.callerIDNumber, call.callerIDName, call.destination);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }), [call]);

  return (
    <div
      ref={dropRef}
      style={isOver ? { outline: '2px dashed #22c55e', borderRadius: 8, background: 'rgba(34, 197, 94, 0.08)' } : undefined}
    >
      <div
        ref={dragRef}
        style={isDragging ? { outline: '2px dashed #3b82f6', borderRadius: 8, cursor: 'grabbing' } : { cursor: 'grab' }}
      >
        <LiveCallItemWrapper
          callUUID={call.callUUID}
          name={call.callerIDName || 'Unknown Caller'}
          ext={call.callerIDNumber || 'Unknown Extension'}
          callDuration=""
          callDescription={getInfoText(call)}
          callees={call.callees || []}
          isRecording={call.isRecording || false}
          destination={call.destination || 'Unknown Destination'}
          callDirection={call.callDirection === 'inbound' ? "inbound" : "outbound" as "inbound" | "outbound"}
          onRecord={onRecord}
          onHangUp={onHangUp}
          onClickCall={onClickCall}
          onTransfer={(callUUID, ext, destination) => onTransfer?.(callUUID, ext, destination, call.callees, call.callerIDNumber, call.callerIDName)}
          callItem={call}
        />
      </div>
    </div>
  );
};

export default React.memo(MemoizedCallItem, (prevProps, nextProps) => {
  return (
    prevProps.call.callUUID === nextProps.call.callUUID &&
    prevProps.call.callerIDName === nextProps.call.callerIDName &&
    prevProps.call.callerIDNumber === nextProps.call.callerIDNumber &&
    prevProps.call.destination === nextProps.call.destination &&
    prevProps.call.callDirection === nextProps.call.callDirection &&
    prevProps.call.isRecording === nextProps.call.isRecording &&
    JSON.stringify(prevProps.call.callees) === JSON.stringify(nextProps.call.callees) &&
    prevProps.call.startTime === nextProps.call.startTime &&
    prevProps.onRecord === nextProps.onRecord &&
    prevProps.onHangUp === nextProps.onHangUp &&
    prevProps.onClickCall === nextProps.onClickCall
  );
});
